import axios from "axios";
import apiService from "./apiService";
import { refuseMailWrite } from "./mailProvider";

const API_BASE_URL = `${process.env.REACT_APP_API_URL}/api/email`;
const EMAIL_ACTION_STATUS_BATCH_SIZE = 100;

const withMailIntegrationQuery = (path, { emailId, mailIntegrationId }) => {
  const query = [`emailId=${encodeURIComponent(emailId)}`];
  if (typeof mailIntegrationId === "string" && mailIntegrationId.length > 0) {
    query.push(`mailIntegrationId=${encodeURIComponent(mailIntegrationId)}`);
  }
  return `${path}?${query.join("&")}`;
};

export const moveEmailService = async (
  getToken,
  { emailId, destinationKey, emailActionData, mailbox, email, mailIntegrationId }
) => {
  const blocked = refuseMailWrite("move_email", { mailbox, email });
  if (blocked) return blocked;

  try {
    const token = await getToken();
    const response = await axios.post(
      `${API_BASE_URL}/move_email`,
      {
        emailId: emailId,
        destinationKey,
        emailActionData: emailActionData || {},
        ...(mailIntegrationId ? { mailIntegrationId } : {}),
      },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error moving email:", error);
    throw error;
  }
};

export const restoreEmailToInboxService = async (getToken, { emailId, mailIntegrationId }) => {
  try {
    const token = await getToken();
    const response = await axios.post(
      `${API_BASE_URL}/restore_email`,
      { emailId, ...(mailIntegrationId ? { mailIntegrationId } : {}) },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error restoring email to inbox:", error);
    throw error;
  }
};

export const getEmailContentService = async (getToken, { emailId, mailIntegrationId }) => {
  try {
    const token = await getToken();
    const response = await axios.get(
      withMailIntegrationQuery(`${API_BASE_URL}/get_email_content`, { emailId, mailIntegrationId }),
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching email content:", error);
    throw error;
  }
};

export const getEmailWebLinkService = async (getToken, { emailId, mailIntegrationId }) => {
  try {
    const token = await getToken();
    const response = await axios.get(
      withMailIntegrationQuery(`${API_BASE_URL}/web_link`, { emailId, mailIntegrationId }),
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    if (error?.response?.status === 404) {
      return null;
    }
    console.error("Error fetching email web link:", error);
    throw error;
  }
};

export const queueUserEmailActionService = async (
  assistantId,
  emailArray,
  { mailbox, email, digestId, batchRequestId } = {}
) => {
  const blocked = refuseMailWrite("queue_user_email_action", { mailbox, email });
  if (blocked) return { ...blocked, jobIds: [] };

  const mailIntegrationId = mailbox?.id ?? mailbox?.mailIntegrationId;
  if (
    typeof digestId !== "string"
    || digestId.length === 0
    || typeof mailIntegrationId !== "string"
    || mailIntegrationId.length === 0
  ) {
    return {
      blocked: true,
      provider: mailbox?.provider ?? email?.provider ?? null,
      userMessage: "Digest mailbox context is unavailable. Refresh your digest before running mail actions again.",
      executedAction: null,
      data: null,
      jobIds: [],
    };
  }

  try {
    const response = await apiService.post(`${API_BASE_URL}/queue_user_email_action`,
      {
        assistantId: assistantId,
        emailArray: emailArray,
        digestId,
        mailIntegrationId,
        ...(batchRequestId ? { batchRequestId } : {}),
      },
    );
    return response.data;
  } catch (error) {
    console.error("Error queuing user email action:", error);
    throw error;
  }
};

export const getEmailActionStatusesService = async (jobIds) => {
  try {
    const statuses = [];
    for (let index = 0; index < jobIds.length; index += EMAIL_ACTION_STATUS_BATCH_SIZE) {
      const response = await apiService.post(`${API_BASE_URL}/email_action_statuses`, {
        jobIds: jobIds.slice(index, index + EMAIL_ACTION_STATUS_BATCH_SIZE),
      });
      statuses.push(...(Array.isArray(response.data?.jobIds) ? response.data.jobIds : []));
    }
    return { jobIds: statuses };
  } catch (error) {
    console.error("Error reconciling email action status:", error);
    throw error;
  }
};
